import type { ScreenSurface } from '@live-stream/shared'
import { CAPTURE_MAX } from '../capture/profile.js'
import { classifyCaptureError, surfaceOf, type CaptureFailure } from './screen-share.js'

/**
 * getDisplayMedia, and nothing else. The store decides what to do with
 * the tracks; this only asks the browser and reads back what it gave.
 */

export interface CapturedScreen {
  stream: MediaStream
  video: MediaStreamTrack
  /** A tab's or the system's sound, when the person ticked the box. */
  audio: MediaStreamTrack | null
  surface: ScreenSurface
}

export class ScreenCaptureError extends Error {
  constructor(readonly failure: CaptureFailure) {
    super(failure)
    this.name = 'ScreenCaptureError'
  }
}

/** Phones, mostly, have no picker; the button says so instead of failing on tap. */
export function supportsScreenCapture(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  )
}

/**
 * Ask for a screen, window or tab. Resolves null when the person closed the
 * picker — that is a decision, not an error. Throws ScreenCaptureError for
 * everything else.
 *
 * The options, each for a reason: 30 fps because text does not need more
 * and video does not get more; a 4K ceiling, so that the browser hands over
 * the screen at its own pixels and text in the recording is as sharp as it is
 * on the screen (the browser scales a capture down before we ever see it, and
 * it used to be to 1080p); audio requested so a tab's sound can come along
 * (the browser offers the tick box only where it can); surfaceSwitching so the
 * person can change tabs from the browser's own bar without stopping;
 * selfBrowserSurface excluded so this page is never offered as a thing to
 * share — the infinite-mirror tunnel.
 *
 * The entire screen is not offered unless asked for (`entireScreen`). A
 * window or a tab is only what is in it; the entire screen is everything on
 * it, including the browser's own "sharing your screen / Stop sharing" bar and
 * any window of ours (the recorder's floating controls among them), and no web
 * page can keep those out of the picture. So the picker offers tabs and
 * windows, and a person who wants the whole screen says so, knowing that.
 */
export async function captureScreen(
  opts: { entireScreen?: boolean } = {},
): Promise<CapturedScreen | null> {
  if (!supportsScreenCapture()) throw new ScreenCaptureError('unsupported')
  const options = {
    video: {
      frameRate: { ideal: 30, max: 30 },
      width: { max: CAPTURE_MAX.width },
      height: { max: CAPTURE_MAX.height },
    },
    audio: true,
    systemAudio: 'include',
    surfaceSwitching: 'include',
    selfBrowserSurface: 'exclude',
    monitorTypeSurfaces: opts.entireScreen ? 'include' : 'exclude',
  }
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(options as DisplayMediaStreamOptions)
  } catch (err) {
    const failure = classifyCaptureError(err)
    if (failure === 'cancelled') return null
    throw new ScreenCaptureError(failure)
  }
  const video = stream.getVideoTracks()[0]
  if (!video) {
    for (const track of stream.getTracks()) track.stop()
    return null
  }
  return {
    stream,
    video,
    audio: stream.getAudioTracks()[0] ?? null,
    surface: surfaceOf(video.getSettings() as { displaySurface?: unknown }),
  }
}
